import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase';
import type { Client, Project, Task } from './database.types';
import { coerceBlockers } from './blockers';

// Normalize the raw jsonb shape of tb_tasks.blocked_by into typed Blocker[]
// on every task we ingest. Handles both the historical string[] shape and
// the current typed-object shape. See src/lib/blockers.ts.
function normalizeTask(raw: Task): Task {
  return { ...raw, blocked_by: coerceBlockers(raw.blocked_by) };
}

// Central data store so sidebar + detail pane + activity share one subscription
// per table instead of N per view. Reduces WebSocket connections and keeps
// everything in sync without prop-drilling.
export type RealtimeStatus = 'connecting' | 'connected' | 'error' | 'closed' | 'offline';

// A task is considered soft-deleted when its tags array contains 'deleted'.
// Soft-deleted tasks are hidden from normal queries but still in the DB,
// recoverable via a "show deleted" toggle in the UI.
export function isDeleted(t: Task): boolean {
  return Array.isArray(t.tags) && t.tags.includes('deleted');
}

// Recent event history for the SyncStatus log popover. Capped at 20.
export interface SyncEvent {
  id: string;
  table: string;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  rowId: string;
  at: string;
}

interface DataStore {
  clients: Client[];
  projects: Project[];
  tasks: Task[];
  allTasks: Task[];
  loading: boolean;
  realtimeStatus: RealtimeStatus;
  lastEventAt: string | null;
  eventCount: number;
  recentEvents: SyncEvent[];
  projectBySlug: Record<string, Project>;
  clientBySlug: Record<string, Client>;
  tasksByProject: Map<string, Task[]>;
  childrenByParent: Map<string, Task[]>;
  subClientsByParent: Map<string, Client[]>;
  isTopLevelClient: (c: Client) => boolean;
  // Optimistic helpers — update local state immediately after a successful
  // mutation, so the UI doesn't have to wait for realtime (which doesn't
  // broadcast INSERTs reliably on tb_tasks).
  upsertTaskLocal: (task: Task) => void;
  patchTaskLocal: (id: string, patch: Partial<Task>) => void;
  removeTaskLocal: (id: string) => void;
  upsertProjectLocal: (project: Project) => void;
  upsertClientLocal: (client: Client) => void;
}

const DataContext = createContext<DataStore | null>(null);

export function DataProvider({ children }: { children: React.ReactNode }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState<RealtimeStatus>(
    typeof navigator !== 'undefined' && navigator.onLine === false
      ? 'offline'
      : 'connecting',
  );
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [eventCount, setEventCount] = useState(0);
  const [recentEvents, setRecentEvents] = useState<SyncEvent[]>([]);

  // Listen to the browser online/offline events so the sync chip reflects
  // network state immediately, not just Supabase websocket state.
  useEffect(() => {
    const onOnline = () => setRealtimeStatus((s) => (s === 'offline' ? 'connecting' : s));
    const onOffline = () => setRealtimeStatus('offline');
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Tick every second so the "synced Xs ago" label updates live even if
  // no new events are coming in.
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const markEvent = (
    table: string,
    eventType: 'INSERT' | 'UPDATE' | 'DELETE',
    rowId: string,
  ) => {
    const now = new Date().toISOString();
    setLastEventAt(now);
    setEventCount((c) => c + 1);
    setRecentEvents((prev) => {
      const next: SyncEvent = {
        id: `${table}-${rowId}-${now}`,
        table,
        eventType,
        rowId,
        at: now,
      };
      return [next, ...prev].slice(0, 20);
    });
  };

  // Optimistic local upserts. AddTaskForm / TaskView / checkbox clicks call
  // these after a successful supabase write so the UI updates even if the
  // realtime channel isn't echoing INSERTs or UPDATEs.
  const upsertTaskLocal = (task: Task) => {
    setTasks((prev) => {
      const exists = prev.some((t) => t.id === task.id);
      return exists ? prev.map((t) => (t.id === task.id ? task : t)) : [task, ...prev];
    });
    markEvent('tb_tasks (local)', 'INSERT', task.id);
  };

  const patchTaskLocal = (id: string, patch: Partial<Task>) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    markEvent('tb_tasks (local)', 'UPDATE', id);
  };

  const removeTaskLocal = (id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    markEvent('tb_tasks (local)', 'DELETE', id);
  };

  const upsertProjectLocal = (project: Project) => {
    setProjects((prev) => {
      const exists = prev.some((p) => p.slug === project.slug);
      return exists ? prev.map((p) => (p.slug === project.slug ? project : p)) : [project, ...prev];
    });
    markEvent('tb_projects (local)', 'INSERT', project.slug);
  };

  const upsertClientLocal = (client: Client) => {
    setClients((prev) => {
      const exists = prev.some((c) => c.slug === client.slug);
      return exists ? prev.map((c) => (c.slug === client.slug ? client : c)) : [client, ...prev];
    });
    markEvent('tb_clients (local)', 'UPDATE', client.slug);
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const [{ data: c }, { data: p }, { data: t }] = await Promise.all([
        supabase.from('tb_clients').select('*').order('display_name'),
        supabase.from('tb_projects').select('*').order('name'),
        supabase.from('tb_tasks').select('*').order('created_at', { ascending: false }),
      ]);
      if (!mounted) return;
      setClients((c as Client[] | null) ?? []);
      setProjects((p as Project[] | null) ?? []);
      setTasks(((t as Task[] | null) ?? []).map(normalizeTask));
      setLoading(false);
    };
    load();

    const sub = supabase
      .channel('data-store')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tb_tasks' },
        (payload) => {
          markEvent(
            payload.table ?? 'unknown',
            payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
            String(
              (payload.new as { id?: string; slug?: string } | undefined)?.id ??
                (payload.new as { id?: string; slug?: string } | undefined)?.slug ??
                (payload.old as { id?: string; slug?: string } | undefined)?.id ??
                '',
            ),
          );
          setTasks((prev) => {
            if (payload.eventType === 'DELETE') {
              return prev.filter((t) => t.id !== (payload.old as Task).id);
            }
            const next = normalizeTask(payload.new as Task);
            const exists = prev.some((t) => t.id === next.id);
            return exists ? prev.map((t) => (t.id === next.id ? next : t)) : [next, ...prev];
          });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tb_projects' },
        (payload) => {
          markEvent(
            payload.table ?? 'unknown',
            payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
            String(
              (payload.new as { id?: string; slug?: string } | undefined)?.id ??
                (payload.new as { id?: string; slug?: string } | undefined)?.slug ??
                (payload.old as { id?: string; slug?: string } | undefined)?.id ??
                '',
            ),
          );
          setProjects((prev) => {
            if (payload.eventType === 'DELETE') {
              return prev.filter((p) => p.slug !== (payload.old as Project).slug);
            }
            const next = payload.new as Project;
            const exists = prev.some((p) => p.slug === next.slug);
            return exists ? prev.map((p) => (p.slug === next.slug ? next : p)) : [...prev, next];
          });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tb_clients' },
        (payload) => {
          markEvent(
            payload.table ?? 'unknown',
            payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
            String(
              (payload.new as { id?: string; slug?: string } | undefined)?.id ??
                (payload.new as { id?: string; slug?: string } | undefined)?.slug ??
                (payload.old as { id?: string; slug?: string } | undefined)?.id ??
                '',
            ),
          );
          setClients((prev) => {
            if (payload.eventType === 'DELETE') {
              return prev.filter((c) => c.slug !== (payload.old as Client).slug);
            }
            const next = payload.new as Client;
            const exists = prev.some((c) => c.slug === next.slug);
            return exists ? prev.map((c) => (c.slug === next.slug ? next : c)) : [...prev, next];
          });
        },
      )
      .subscribe((status) => {
        // status is one of: SUBSCRIBED | CHANNEL_ERROR | CLOSED | TIMED_OUT
        if (status === 'SUBSCRIBED') setRealtimeStatus('connected');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setRealtimeStatus('error');
        else if (status === 'CLOSED') setRealtimeStatus('closed');
      });

    return () => {
      mounted = false;
      sub.unsubscribe();
    };
  }, []);

  const projectBySlug = Object.fromEntries(projects.map((p) => [p.slug, p]));
  const clientBySlug = Object.fromEntries(clients.map((c) => [c.slug, c]));
  const visibleTasks = tasks.filter((t) => !isDeleted(t));
  const tasksByProject = new Map<string, Task[]>();
  const childrenByParent = new Map<string, Task[]>();
  for (const t of visibleTasks) {
    if (t.parent_task_id) {
      const arr = childrenByParent.get(t.parent_task_id) ?? [];
      arr.push(t);
      childrenByParent.set(t.parent_task_id, arr);
    } else {
      const arr = tasksByProject.get(t.project_slug) ?? [];
      arr.push(t);
      tasksByProject.set(t.project_slug, arr);
    }
  }

  const subClientsByParent = new Map<string, Client[]>();
  for (const c of clients) {
    const parentSlug =
      typeof c.context === 'object' && c.context && 'parent_slug' in c.context
        ? (c.context as { parent_slug?: string }).parent_slug
        : undefined;
    if (parentSlug) {
      const arr = subClientsByParent.get(parentSlug) ?? [];
      arr.push(c);
      subClientsByParent.set(parentSlug, arr);
    }
  }

  const isTopLevelClient = (c: Client): boolean => {
    const parentSlug =
      typeof c.context === 'object' && c.context && 'parent_slug' in c.context
        ? (c.context as { parent_slug?: string }).parent_slug
        : undefined;
    return !parentSlug;
  };

  return (
    <DataContext.Provider
      value={{
        clients,
        projects,
        tasks: visibleTasks,
        allTasks: tasks,
        loading,
        realtimeStatus,
        lastEventAt,
        eventCount,
        recentEvents,
        projectBySlug,
        clientBySlug,
        tasksByProject,
        childrenByParent,
        subClientsByParent,
        isTopLevelClient,
        upsertTaskLocal,
        patchTaskLocal,
        removeTaskLocal,
        upsertProjectLocal,
        upsertClientLocal,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData(): DataStore {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used inside DataProvider');
  return ctx;
}
